import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@/apollo';
import { GET_APPLICATION_SETTINGS } from '../graphql/app-settings';
import { useAuth } from './AuthContext';

interface SessionSettings {
  sessionMaxDurationHours: number;
  sessionCleanupIntervalHours: number;
  maxConcurrentSessions: number;
  suspiciousActivityThreshold: number;
  sessionInactivityDays: number;
  enableSessionAnalytics: boolean;
  enableSessionFingerprinting: boolean;
  enableGeoTracking: boolean;
}

interface GeolocationSettings {
  enabled: boolean;
  mode: string;
  countries: string[];
  availableCountries: Array<{ code: string; name: string }>;
}

interface ApplicationSettings {
  applicationName: string;
  companyName: string;
  supportEmail: string;
  bugReportEmail: string;
  defaultTimezone: string;
  notesEnabled: boolean;
  bulletinsEnabled: boolean;
  sessionSettings: SessionSettings;
  geolocationSettings?: GeolocationSettings;
}

interface ApplicationSettingsContextType {
  settings: ApplicationSettings | null;
  loading: boolean;
  error: any;
  refreshSettings: () => void;
}

const DEFAULT_SESSION_SETTINGS: SessionSettings = {
  sessionMaxDurationHours: 168,
  sessionCleanupIntervalHours: 1,
  maxConcurrentSessions: 5,
  suspiciousActivityThreshold: 50,
  sessionInactivityDays: 30,
  enableSessionAnalytics: true,
  enableSessionFingerprinting: true,
  enableGeoTracking: false,
};

const DEFAULT_SETTINGS: ApplicationSettings = {
  applicationName: 'Provider Selection Platform',
  companyName: 'Healthcare Solutions Inc.',
  supportEmail: 'support@healthcaresolutions.com',
  bugReportEmail: 'support@healthcaresolutions.com',
  defaultTimezone: 'America/New_York',
  notesEnabled: false, // Default to disabled until database settings load
  bulletinsEnabled: false, // Default to disabled until database settings load
  sessionSettings: DEFAULT_SESSION_SETTINGS,
};

const ApplicationSettingsContext = createContext<ApplicationSettingsContextType | undefined>(undefined);

interface ApplicationSettingsProviderProps {
  children: ReactNode;
}

export const ApplicationSettingsProvider: React.FC<ApplicationSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<ApplicationSettings | null>(null);
  const { isAuthenticated } = useAuth();
  
  const { data, loading, error, refetch } = useQuery(GET_APPLICATION_SETTINGS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated, // Skip query if user is not authenticated
    errorPolicy: 'all' // Continue even if there's an error
  });

  const refreshSettings = () => {
    refetch();
  };

  useEffect(() => {
    if (data?.applicationSettings) {
      setSettings(data.applicationSettings);
    } else if (error) {
      // Only use default settings on error, not on initial load
      console.warn('Failed to load application settings, using defaults:', error);
      setSettings(DEFAULT_SETTINGS);
    }
  }, [data, error]);

  const value: ApplicationSettingsContextType = {
    settings,
    loading,
    error,
    refreshSettings
  };



  return (
    <ApplicationSettingsContext.Provider value={value}>
      {children}
    </ApplicationSettingsContext.Provider>
  );
};

export const useApplicationSettings = (): ApplicationSettingsContextType => {
  const context = useContext(ApplicationSettingsContext);
  if (context === undefined) {
    // Return default context instead of throwing error
    // This allows components to work even if provider isn't available
    console.warn('useApplicationSettings called outside ApplicationSettingsProvider, using defaults');
    return {
      settings: DEFAULT_SETTINGS,
      loading: false,
      error: null,
      refreshSettings: () => Promise.resolve(),
    };
  }
  return context;
};
